import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const relatedArticles = id ? getRelatedArticles(id, 4) : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-32">
          <span className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-4">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h2 className="text-xl font-semibold text-foreground-900 mb-2">文章不存在</h2>
          <p className="text-foreground-600 mb-6">这篇文章可能已经被删除或移除了</p>
          <Link
            to="/"
            className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const formattedDate = new Date(article.publishedAt).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '爆料吃瓜',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://bagua.example.com/article/${article.id}`,
    },
  };

  return (
    <>
      <head>
        <title>{`${article.title} - 爆料吃瓜`}</title>
        <meta name="description" content={article.excerpt} />
        <meta name="keywords" content={article.tags.join(',')} />
        <meta name="author" content={article.author} />
        <link rel="canonical" href={`https://bagua.example.com/article/${article.id}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${article.title} - 爆料吃瓜`} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:image" content={article.coverImage} />
        <meta property="og:url" content={`https://bagua.example.com/article/${article.id}`} />
        <meta property="article:published_time" content={article.publishedAt} />
        <meta property="article:author" content={article.author} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${article.title} - 爆料吃瓜`} />
        <meta name="twitter:description" content={article.excerpt} />
        <meta name="twitter:image" content={article.coverImage} />
      </head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <article className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-8">
            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {article.isHot && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                    <i className="ri-fire-line text-xs"></i> 热门爆料
                  </span>
                )}
                {article.isNew && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                    <i className="ri-sparkling-line text-xs"></i> 新鲜出炉
                  </span>
                )}
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors"
                >
                  <i className="ri-price-tag-3-line"></i>
                  {article.category}
                </Link>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-600 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-user-line"></i>
                  </span>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {formattedDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {formatViews(article.views)} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i> {article.comments} 评论
                </span>
              </div>
            </header>

            {/* Cover Image */}
            <div className="rounded-xl overflow-hidden mb-8 aspect-[16/9]">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 爆料吃瓜`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose prose-lg max-w-none mb-10"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="flex items-center gap-2 flex-wrap mb-10 pt-6 border-t border-background-200/60">
              <span className="text-sm text-foreground-600 font-medium">
                <i className="ri-price-tag-3-line"></i> 标签：
              </span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-600"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Share */}
            <div className="flex items-center gap-3 mb-10">
              <span className="text-sm text-foreground-600 font-medium">分享到：</span>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-green-50 text-green-500 hover:bg-green-100 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-link"></i>
              </button>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 pb-16">
              <div className="max-w-6xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-500 text-background-50">
                    <i className="ri-links-line"></i>
                  </span>
                  <h2 className="text-xl font-bold text-foreground-950">相关爆料</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {relatedArticles.map((related) => (
                    <ArticleCard key={related.id} article={related} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}